import {
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { NgIf } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { Clipboard } from '@capacitor/clipboard';
import { toDataURL } from 'qrcode';
import {
  CustomModalOptions,
  ModalAbstractComponent
} from '@bling-fe/shared/modals/base-modal';
import { marker as _ } from '@bling-fe/shared/utils';
import { getCurrentTenant } from '../../core/states/tenant-state/tenant.selectors';
import { NotificationService } from '../../shared/services/notification.service';
import { ShareService } from '../../shared/services/share.service';

export type ShareKind = 'video' | 'audio' | 'image' | 'document' | 'folder';

export interface ShareModalProps {
  title: string;
  url: string;
  kind: ShareKind;
}

const SCAN_HINTS: Record<ShareKind, string> = {
  video: _('COMPONENTS.SHARE.SCAN_HINT.VIDEO'),
  audio: _('COMPONENTS.SHARE.SCAN_HINT.AUDIO'),
  image: _('COMPONENTS.SHARE.SCAN_HINT.IMAGE'),
  document: _('COMPONENTS.SHARE.SCAN_HINT.DOCUMENT'),
  folder: _('COMPONENTS.SHARE.SCAN_HINT.FOLDER')
};

// The printed page lives in a frame of its own, away from the app's styles.
// ponytail: system fonts; Inter would have to be loaded into the frame first.
const POSTER_CSS = `
  @page { size: A4; margin: 0; }
  html, body { margin: 0; }
  .poster {
    box-sizing: border-box; width: 210mm; height: 297mm; overflow: hidden;
    padding: 22mm 20mm 16mm; display: flex; flex-direction: column;
    font-family: Inter, "Helvetica Neue", Arial, sans-serif; color: #000;
  }
  .poster-logo { height: 14mm; max-width: 70mm; object-fit: contain; object-position: left; align-self: flex-start; }
  .poster-title {
    margin: 22mm 0 0; font-size: 54pt; line-height: 1.05; font-weight: 800; letter-spacing: -0.02em;
    overflow-wrap: break-word; text-wrap: balance;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  }
  .poster-qr { display: block; margin-top: auto; width: 100mm; image-rendering: pixelated; }
  .poster-hint { margin: 10mm 0 0; max-width: 150mm; font-size: 20pt; line-height: 1.35; font-weight: 500; }
  .poster-url { margin: 12mm 0 0; font-size: 10pt; color: #555; }
`;

/**
 * The link of a document or a folder, with a QR code that opens it in the
 * app. On the web the code can be printed as an A4 notice or downloaded; a
 * webview can do neither, so the app hands the link to the phone's share menu
 * instead.
 */
@Component({
  selector: 'fl-share-modal',
  templateUrl: './share-modal.component.html',
  styleUrls: ['./share-modal.component.scss'],
  imports: [NgIf, IonicModule, TranslateModule, SvgIconComponent],
  providers: [
    { provide: ModalAbstractComponent, useExisting: ShareModalComponent }
  ]
})
export class ShareModalComponent
  extends ModalAbstractComponent<ShareModalProps, void>
  implements OnInit
{
  public static override modalOptions: CustomModalOptions = {
    cssClass: 'auto-height bottom-sheet-modal share-modal'
  };

  @ViewChild('poster') poster?: ElementRef<HTMLElement>;

  readonly native = Capacitor.isNativePlatform();
  /** Shown and printed: the bare code, the card and the page are its margin. */
  qr?: string;
  /** Downloaded: the code with its quiet zone, to place anywhere. */
  private file?: string;
  linkCopied = false;
  private copiedTimer?: ReturnType<typeof setTimeout>;
  private sharing?: Promise<void>;

  private readonly tenant = toSignal(inject(Store).select(getCurrentTenant));
  private notifications = inject(NotificationService);
  private shareService = inject(ShareService);

  get logo(): string | undefined {
    const tenant = this.tenant();
    return tenant && tenant !== 'loading'
      ? tenant.tenant_setting?.app_logo || undefined
      : undefined;
  }

  get displayUrl(): string {
    return this.props.url.replace(/^https?:\/\//, '');
  }

  get scanHint(): string {
    return SCAN_HINTS[this.props.kind];
  }

  async ngOnInit() {
    const { url } = this.props;
    [this.qr, this.file] = await Promise.all([
      toDataURL(url, { margin: 0, width: 512 }),
      toDataURL(url, { margin: 4, width: 1024 })
    ]);
  }

  copyLink() {
    Clipboard.write({ string: this.props.url }).then(
      () => this.flashCopied(),
      () => this.notifications.error(_('COMMON.LINK.COPY_FAILED'))
    );
  }

  download() {
    if (!this.file) return;
    const link = document.createElement('a');
    link.href = this.file;
    link.download = `QR-Code ${this.props.title.replace(/[\\/:*?"<>|]/g, '')}.png`;
    link.click();
  }

  async print() {
    if (!this.poster) return;
    const frame = document.createElement('iframe');
    frame.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    document.body.append(frame);
    const doc = frame.contentDocument as Document;
    const win = frame.contentWindow as Window;
    doc.title = this.props.title;
    const style = doc.createElement('style');
    style.textContent = POSTER_CSS;
    doc.head.append(style);
    doc.body.append(doc.importNode(this.poster.nativeElement, true));
    await Promise.all(
      Array.from(doc.images, img => img.decode().catch(() => img.remove()))
    );
    win.addEventListener('afterprint', () => frame.remove());
    win.print();
  }

  shareLink() {
    this.sharing ??= this.shareService
      .share({ title: this.props.title, url: this.props.url })
      .then(
        () => undefined,
        () => this.notifications.error(_('ERROR.GLOBAL.UNKNOWN'))
      )
      .finally(() => (this.sharing = undefined));
  }

  // The button says so in place for a moment; a toast would land behind the
  // sheet on a phone.
  private flashCopied() {
    clearTimeout(this.copiedTimer);
    this.linkCopied = true;
    this.copiedTimer = setTimeout(() => (this.linkCopied = false), 2000);
  }
}
